/**
 * 研究メモ：目次のどの頁・何番目に表示されていたスポットの詳細が開かれたか。
 * 並びが現在地で変わるので、詳細を開いた事実（view_logs）だけでは「どこに見えていたか」が復元できない。
 * 送る RPC はまだ無いので、端末のメモリにだけ置く（再読込で消える）。RPC ができたら entries() を送る。
 */
export interface DisplayLayout {
  areaId: string;
  /** 表示した順の spot_id */
  order: number[];
  /** 'distance'＝現在地から近い順 / 'server'＝サーバーの順（位置が無いとき） */
  orderedBy: 'distance' | 'server';
  gpsStatus: string;
  /** 並べた時刻（ISO） */
  at: string;
  /** spot_id → 並べたときの距離（m）。位置が無ければ空 */
  distances: Record<number, number>;
}

export interface DisplayRecord {
  seq: number;
  /** 詳細を開いた時刻（ISO） */
  openedAt: string;
  spotId: number;
  areaId: string;
  /** 0始まり */
  pageIndex: number;
  rowInPage: number;
  orderIndex: number;
  orderedBy: DisplayLayout['orderedBy'];
  gpsStatus: string;
  distanceM: number | null;
  layoutAt: string;
  pageSize: number;
}

const MAX = 500;

class DisplayLog {
  private layout: DisplayLayout | null = null;
  private rows: DisplayRecord[] = [];
  private seq = 0;

  setLayout(l: DisplayLayout): void {
    this.layout = l;
  }

  currentLayout(): DisplayLayout | null {
    return this.layout;
  }

  /** 目次から詳細を開いたとき。目次を経由しない（地図から来た等）ときは何もしない */
  recordOpen(spotId: number, pageSize: number): DisplayRecord | null {
    const l = this.layout;
    if (!l) return null;
    const orderIndex = l.order.indexOf(spotId);
    if (orderIndex < 0) return null;
    const rec: DisplayRecord = {
      seq: ++this.seq,
      openedAt: new Date().toISOString(),
      spotId,
      areaId: l.areaId,
      pageIndex: Math.floor(orderIndex / pageSize),
      rowInPage: orderIndex % pageSize,
      orderIndex,
      orderedBy: l.orderedBy,
      gpsStatus: l.gpsStatus,
      distanceM: l.distances[spotId] ?? null,
      layoutAt: l.at,
      pageSize,
    };
    this.rows.push(rec);
    if (this.rows.length > MAX) this.rows.splice(0, this.rows.length - MAX);
    return rec;
  }

  entries(): readonly DisplayRecord[] {
    return this.rows;
  }

  clear(): void {
    this.rows = [];
    this.layout = null;
    this.seq = 0;
  }
}

export const displayLog = new DisplayLog();
